// @ts-strict-ignore
import { forEach, forIn, reduce, unset } from 'lodash';
import {
    AppsAddedSuccessAction,
    AppsDeletedAction,
    AppStateUpdatedAction,
    AppsUpdatedAction,
    APPS_ADDED_SUCCESS,
    APPS_DELETED,
    APPS_UPDATED,
    APP_STATE_UPDATED,
    ConnectedAppEntityResponse,
    DeletedAppEntityPayload,
    AppStateResponse,
    FETCH_APP_STATE_SUCCESS,
    FetchAppStateSuccessAction,
} from '../actions/app';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { OrgStateUpdatedAction, ORG_STATE_UPDATED } from '../actions/organization';
import {
    BaseConnectedRelease,
    DeleteReleaseEntityAction,
    DeleteReleaseEntityPayload,
    DeleteReleasesAction,
    DELETE_RELEASES,
    DELETE_RELEASE_ENTITY,
    FetchReleasesSuccessAction,
    FETCH_RELEASES_SUCCESS,
    NewReleaseSuccessAction,
    NEW_RELEASE_SUCCESS,
    ReleaseStateUpdatedAction,
    ReleasesUpdatedAction,
    RELEASES_UPDATED,
    RELEASE_STATE_UPDATED,
} from '../actions/release';
import { ClearStateAction, CLEAR_STATE, FetchStateSuccessAction, FETCH_STATE_SUCCESS } from '../actions/state';
import { ReleaseEntity } from '../types';
import { StringKeyedDictionary } from '../typescriptHelpers';

interface Release {
    id: string;
}

interface App {
    id: string;
    releases: StringKeyedDictionary<Release>;
}

interface Org {
    id: string;
    apps: StringKeyedDictionary<App>;
}

type UUIDMappingActionType =
    | FetchStateSuccessAction
    | FetchAppStateSuccessAction
    | ReleaseStateUpdatedAction
    | AppStateUpdatedAction
    | OrgStateUpdatedAction
    | NewReleaseSuccessAction
    | ReleasesUpdatedAction
    | DeleteReleaseEntityAction
    | DeleteReleasesAction
    | LogoutSuccessAction
    | AppsAddedSuccessAction
    | AppsUpdatedAction
    | AppsDeletedAction
    | ClearStateAction
    | FetchReleasesSuccessAction;

type UUIDMappingsType = StringKeyedDictionary<Org>;

const generateUUIDMappings = (state: UUIDMappingsType, action: FetchStateSuccessAction) => {
    const orgs = action.payload.organizations;

    const updatedState = {};

    orgs.forEach((org) => {
        const apps = {};

        org.apps.forEach((app) => {
            apps[app.appId] = {
                id: app.id,
                releases: {},
            };
        });

        updatedState[org.orgId] = {
            id: org.id,
            apps,
        };
    });

    return updatedState;
};

const deleteReleaseAndUpdateUUIDMappings = (state: UUIDMappingsType, payload: DeleteReleaseEntityPayload) => {
    if (!payload.orgIdParam || !payload.appIdParam) {
        return state;
    }

    const prevOrg = state[payload.orgIdParam] ?? {};
    const prevApps = state[payload.orgIdParam]?.apps ?? {};
    const prevApp = prevApps[payload.appIdParam] ?? {};
    const releases = prevApps[payload.appIdParam]?.releases ?? {};

    // find releaseId key for id...

    let releasesIdKeyToUnset = null;

    forIn(releases, (value, key) => {
        if (value.id === payload.releaseId) {
            releasesIdKeyToUnset = key;
        }
    });

    if (releasesIdKeyToUnset) {
        unset(releases, releasesIdKeyToUnset);
    }

    return {
        ...state,
        [payload.orgIdParam]: {
            ...prevOrg,
            id: payload.orgId,
            apps: {
                ...prevApps,
                [payload.appIdParam]: {
                    ...prevApp,
                    id: payload.appId,
                    releases: {
                        ...releases,
                    },
                },
            },
        },
    };
};

const deleteReleasesAndUpdateUUIDMappings = (state: UUIDMappingsType, payload: DeleteReleaseEntityPayload[]) => {
    return reduce(
        payload,
        (currState, deletedReleaseEntityPayload) => {
            return deleteReleaseAndUpdateUUIDMappings(currState, deletedReleaseEntityPayload);
        },
        state,
    );
};

const updateAppsUUIDMappings = (state: UUIDMappingsType, payload: ConnectedAppEntityResponse[]) => {
    return reduce(
        payload,
        (currentState, connectedApp) => {
            return updateAppUUIDMappings(currentState, connectedApp);
        },
        state,
    );
};

const deleteAppAndUpdateUUIDMappings = (state: UUIDMappingsType, payload: DeletedAppEntityPayload) => {
    if (!payload.orgIdParam) {
        return state;
    }

    const prevOrg = state[payload.orgIdParam] ?? {};
    const apps = state[payload.orgIdParam]?.apps ?? {};

    // find appId key for id...

    let appIdKeyToUnset = null;

    forIn(apps, (value, key) => {
        if (value.id === payload.appId) {
            appIdKeyToUnset = key;
        }
    });

    if (appIdKeyToUnset) {
        unset(apps, appIdKeyToUnset);
    }

    return {
        ...state,
        [payload.orgIdParam]: {
            ...prevOrg,
            id: payload.orgId,
            apps: {
                ...apps,
            },
        },
    };
};

const deleteAppsAndUpdateUUIDMappings = (state: UUIDMappingsType, payload: DeletedAppEntityPayload[]) => {
    return reduce(
        payload,
        (currState, deletedAppEntityPayload) => {
            return deleteAppAndUpdateUUIDMappings(currState, deletedAppEntityPayload);
        },
        state,
    );
};

const updateReleasesUUIDMappings = (state: UUIDMappingsType, action: ReleasesUpdatedAction) => {
    return reduce(
        action.payload,
        (currentState, connectedRelease) => {
            return updateReleaseUUIDMappings(currentState, connectedRelease, [connectedRelease.release]);
        },
        state,
    );
};

const updateReleaseUUIDMappings = (
    state: UUIDMappingsType,
    payload: BaseConnectedRelease | AppStateResponse,
    releases: ReleaseEntity[],
) => {
    if (!payload.orgIdParam || !payload.appIdParam) {
        return state;
    }

    const prevOrg = state[payload.orgIdParam] ?? {};
    const prevApps = state[payload.orgIdParam]?.apps ?? {};
    const prevApp = prevApps[payload.appIdParam] ?? {};
    const prevReleases = prevApps[payload.appIdParam]?.releases ?? {};

    const updatedReleases = {};

    forEach(releases, (release) => {
        updatedReleases[release.releaseId] = { id: release.id };
    });

    return {
        ...state,
        [payload.orgIdParam]: {
            ...prevOrg,
            id: payload.orgId,
            apps: {
                ...prevApps,
                [payload.appIdParam]: {
                    ...prevApp,
                    id: payload.appId,
                    releases: {
                        ...prevReleases,
                        ...updatedReleases,
                    },
                },
            },
        },
    };
};

const updateAppUUIDMappings = (state: UUIDMappingsType, payload: ConnectedAppEntityResponse) => {
    if (!payload.orgIdParam) {
        return state;
    }

    const prevOrgState = state[payload.orgIdParam] ?? {};
    const prevApps = state[payload.orgIdParam]?.apps ?? {};
    const prevAppState = state[payload.orgIdParam]?.apps[payload.app.appId] ?? {};
    const prevReleasesState = state[payload.orgIdParam]?.apps[payload.app.appId]?.releases ?? {};

    return {
        ...state,
        [payload.orgIdParam]: {
            ...prevOrgState,
            id: payload.orgId,
            apps: {
                ...prevApps,
                [payload.app.appId]: {
                    ...prevAppState,
                    id: payload.app.id,
                    releases: {
                        ...prevReleasesState,
                    },
                },
            },
        },
    };
};

const updateOrgUUIDMappings = (state: UUIDMappingsType, action: OrgStateUpdatedAction) => {
    const updatedOrg = action.payload;
    const apps = state[updatedOrg.orgId]?.apps ?? {};

    return {
        ...state,
        [updatedOrg.orgId]: {
            id: updatedOrg.id,
            apps: {
                ...apps,
            },
        },
    };
};

/* A mapping of user-friendly orgIds, appIds, and releaseIds, to their database ids
    Note that user-friendly ids are not unique on their own, but they are unique in the context of their corresponding orgId, and appId
    For example, releaseId: 1.4.0 is not unique, but orgId: 1, appId: 1, releaseId: 1.4.0 is unique.


    {
        org-id: {
            id: 1,
            apps: {
                app-id: {
                    id: 1,
                    releases: {
                        1.0.0: {
                            id: 1
                        }
                    }
                }
            }
        },
    }
*/

const uuidMappingReducer: (state: UUIDMappingsType, action: UUIDMappingActionType) => StringKeyedDictionary<Org> = (
    state = {},
    action: UUIDMappingActionType,
) => {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return generateUUIDMappings(state, action);
        case FETCH_APP_STATE_SUCCESS:
            return updateReleaseUUIDMappings(state, action.payload, action.payload.app.releases);
        case FETCH_RELEASES_SUCCESS:
            return updateReleaseUUIDMappings(state, action.payload, action.payload.releases);
        case NEW_RELEASE_SUCCESS:
        case RELEASE_STATE_UPDATED:
            return updateReleaseUUIDMappings(state, action.payload, [action.payload.release]);
        case APP_STATE_UPDATED:
            return updateAppUUIDMappings(state, action.payload);
        case ORG_STATE_UPDATED:
            return updateOrgUUIDMappings(state, action);
        case RELEASES_UPDATED:
            return updateReleasesUUIDMappings(state, action);
        case APPS_UPDATED:
        case APPS_ADDED_SUCCESS:
            return updateAppsUUIDMappings(state, action.payload);
        case DELETE_RELEASE_ENTITY:
            return deleteReleaseAndUpdateUUIDMappings(state, action.payload);
        case DELETE_RELEASES:
            return deleteReleasesAndUpdateUUIDMappings(state, action.payload);
        case APPS_DELETED:
            return deleteAppsAndUpdateUUIDMappings(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

export default uuidMappingReducer;
